// Real (Firebase) auth session handling. Vite aliases this whole file to
// services/mock/auth.service when VITE_USE_MOCK_AUTH=true — authStore.ts
// itself never branches on mock vs real, it only calls this facade.
import {
    signInWithPopup,
    linkWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    linkWithCredential,
    signInWithCustomToken,
    EmailAuthProvider,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    type AuthCredential,
    type User,
} from 'firebase/auth';
import { SignInMethodsSchema, type CreateUserInput, type SignInMethods } from '@kaeliux/shared';
import { auth } from '../config/firebase';
import {
    getUserProfile,
    createOrUpdateProfile,
    touchLastLogin,
    updateProfileEmail,
    type ProfileInput,
} from './profile.service';
import { createAuthProvider, credentialFromProviderError, type SocialProvider } from './authProviders';
import { ACCOUNT_EXISTS_CODE, authErrorCode, authErrorEmail } from './authErrors';
import { forgetEmailForSignIn, rememberEmailForSignIn, storedEmailForSignIn } from './emailLinkStorage';
import type { AuthResult } from './authSession.types';
import { isGuestUser } from './authSession';
import { authedPost, REQUEST_TIMEOUT_MS } from './http';

export { storedEmailForSignIn };

/**
 * A provider credential that was refused because its email already belongs
 * to another account. It is linked onto that account the next time the
 * player signs in with the matching email.
 */
let pendingLink: { email: string; credential: AuthCredential } | null = null;

export function hasPendingLink(): boolean {
    return pendingLink !== null;
}

export function cancelPendingLink(): void {
    pendingLink = null;
}

function rememberPendingLink(provider: SocialProvider, error: unknown): void {
    if (authErrorCode(error) !== ACCOUNT_EXISTS_CODE) return;
    const email = authErrorEmail(error);
    const credential = credentialFromProviderError(provider, error);
    if (email && credential) pendingLink = { email, credential };
}

async function linkPendingCredential(user: User): Promise<void> {
    const pending = pendingLink;
    pendingLink = null;
    if (!pending || user.email?.toLowerCase() !== pending.email.toLowerCase()) return;
    try {
        await linkWithCredential(user, pending.credential);
    } catch (error) {
        console.error('Failed to link the pending sign-in method:', error);
    }
}

async function ensureProfile(user: User): Promise<AuthResult> {
    const existing = await getUserProfile(user.uid);
    if (!existing) {
        await createOrUpdateProfile(user.uid, {
            displayName: user.displayName || user.email?.split('@')[0] || 'Commander',
            email: user.email || '',
        });
    } else {
        await touchLastLogin(user.uid);
    }
    return { user, profile: await getUserProfile(user.uid) };
}

async function finishSignIn(user: User): Promise<AuthResult> {
    await linkPendingCredential(user);
    return ensureProfile(user);
}

async function sendVerification(user: User): Promise<void> {
    try {
        await sendEmailVerification(user);
    } catch (error) {
        console.error('Failed to send the verification email:', error);
    }
}

function requireGuest(): User {
    const user = auth.currentUser;
    if (!user || !isGuestUser(user)) throw new Error('Only guest accounts can be upgraded.');
    return user;
}

/** The fresh ID token tells the server the account is registered now, which starts the hostile-mission wait (issue #332). */
async function afterGuestUpgrade(user: User): Promise<AuthResult> {
    await user.getIdToken(true);
    if (user.email) await updateProfileEmail(user.uid, user.email);
    return { user, profile: await getUserProfile(user.uid) };
}

/**
 * How long the sign-in methods may take. The sign-in pages show only a spinner
 * until they arrive, and a connection that hangs without a reset never settles
 * on its own, so give up early and let the page fall back.
 */
export const SIGN_IN_METHODS_TIMEOUT_MS = 8_000;

/** The sign-in methods switched on in this project's Firebase Authentication settings. */
export async function getSignInMethods(): Promise<SignInMethods> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SIGN_IN_METHODS_TIMEOUT_MS);
    try {
        const res = await fetch('/getSignInMethods', { signal: controller.signal });
        if (!res.ok) throw new Error(`Request to /getSignInMethods failed: ${res.status}`);
        return SignInMethodsSchema.parse(await res.json());
    } catch (error) {
        if (controller.signal.aborted) {
            throw new Error(`Request to /getSignInMethods timed out after ${SIGN_IN_METHODS_TIMEOUT_MS / 1000} s`);
        }
        throw error;
    } finally {
        clearTimeout(timer);
    }
}

export async function login(): Promise<AuthResult> {
    const credential = await signInWithPopup(auth, new GoogleAuthProvider());
    const user = credential.user;
    const profile = await getUserProfile(user.uid);
    return { user, profile };
}

export async function register(data: CreateUserInput): Promise<AuthResult> {
    const credential = await signInWithPopup(auth, new GoogleAuthProvider());
    const user = credential.user;
    const profileInput: ProfileInput = {
        displayName: data.displayName || user.displayName || user.email?.split('@')[0] || 'Commander',
        email: user.email || data.email,
    };
    await createOrUpdateProfile(user.uid, profileInput);
    const profile = await getUserProfile(user.uid);
    return { user, profile };
}

export async function socialLogin(provider: SocialProvider): Promise<AuthResult> {
    let user: User;
    try {
        ({ user } = await signInWithPopup(auth, createAuthProvider(provider)));
    } catch (error) {
        rememberPendingLink(provider, error);
        throw error;
    }
    return finishSignIn(user);
}

export async function passwordSignIn(email: string, password: string): Promise<AuthResult> {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    return finishSignIn(user);
}

export async function passwordSignUp(email: string, password: string): Promise<AuthResult> {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await sendVerification(user);
    return finishSignIn(user);
}

/** Resolves for unknown addresses too, so the form can't be used to probe which emails have accounts. */
export async function sendPasswordReset(email: string): Promise<void> {
    try {
        await sendPasswordResetEmail(auth, email);
    } catch (error) {
        if (authErrorCode(error) !== 'auth/user-not-found') throw error;
    }
}

export async function sendEmailSignInLink(email: string): Promise<void> {
    await sendSignInLinkToEmail(auth, email, {
        url: `${window.location.origin}/login/email-link`,
        handleCodeInApp: true,
    });
    rememberEmailForSignIn(email);
}

export function isEmailSignInLink(href: string): boolean {
    return isSignInWithEmailLink(auth, href);
}

export async function completeEmailLinkSignIn(email: string, href: string): Promise<AuthResult> {
    const { user } = await signInWithEmailLink(auth, email, href);
    forgetEmailForSignIn();
    return finishSignIn(user);
}

/**
 * POSTs to a public guest endpoint and signs in with the custom token it
 * returns. Shared by guest sign-up and guest-ID sign-in, which both pass a
 * Turnstile token the server checks before doing anything.
 */
async function signInThroughServer(path: string, body: object, tooSlow: string, failed: string): Promise<User> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let res: Response;
    let answer: { token?: string; error?: string };
    try {
        res = await fetch(path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: controller.signal,
        });
        answer = (await res.json().catch(() => ({}))) as typeof answer;
    } catch (error) {
        if (controller.signal.aborted) throw new Error(tooSlow);
        throw error;
    } finally {
        clearTimeout(timer);
    }
    if (!res.ok || !answer.token) throw new Error(answer.error ?? failed);
    const { user } = await signInWithCustomToken(auth, answer.token);
    return user;
}

/**
 * Starts a guest game. The server creates the guest account once the
 * Turnstile check passes; Firebase anonymous sign-in is not used, so the
 * check can't be skipped.
 */
export async function guestSignIn(turnstileToken: string): Promise<AuthResult> {
    const user = await signInThroughServer(
        '/createGuestAccount',
        { turnstileToken },
        'Starting a guest game took too long. Please try again.',
        'Starting a guest game failed. Please try again.',
    );
    return finishSignIn(user);
}

/**
 * Signs a guest back into their account with the guest ID they saved
 * (issue #332). The server exchanges the ID for a custom token for that uid.
 */
export async function signInWithGuestId(guestId: string, turnstileToken: string): Promise<AuthResult> {
    const user = await signInThroughServer(
        '/signInWithGuestId',
        { guestId, turnstileToken },
        'Signing in with that guest ID took too long. Please try again.',
        'Signing in with that guest ID failed. Please try again.',
    );
    return finishSignIn(user);
}

/** The signed-in guest's guest ID, allocated on first request. */
export async function getGuestSignInId(): Promise<string> {
    const { guestId } = await authedPost<{ guestId: string }>('/getGuestSignInId', {});
    return guestId;
}

/** Linking keeps the guest's uid, so every planet and fleet carries over to the real account. */
export async function upgradeGuestWithPassword(email: string, password: string): Promise<AuthResult> {
    const { user } = await linkWithCredential(requireGuest(), EmailAuthProvider.credential(email, password));
    await sendVerification(user);
    return afterGuestUpgrade(user);
}

export async function upgradeGuestWithProvider(provider: SocialProvider): Promise<AuthResult> {
    const { user } = await linkWithPopup(requireGuest(), createAuthProvider(provider));
    return afterGuestUpgrade(user);
}

export async function resendVerificationEmail(): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('You need to be signed in to verify your email.');
    await sendEmailVerification(user);
}

/** Re-reads the account (e.g. after the player clicks the verification link) and refreshes the ID token so its claims match. */
export async function refreshUser(): Promise<User | null> {
    const user = auth.currentUser;
    if (!user) return null;
    await user.reload();
    await user.getIdToken(true);
    return user;
}

export async function logoutSession(): Promise<void> {
    pendingLink = null;
    await signOut(auth);
}

/**
 * Fires once immediately with the current session (or `null`), then again on
 * every future Firebase auth state change. Returns an unsubscribe function.
 */
export function subscribeToAuthChanges(onChange: (result: AuthResult | null) => void): () => void {
    return onAuthStateChanged(auth, async (user: User | null) => {
        if (!user) {
            onChange(null);
            return;
        }
        const profile = await getUserProfile(user.uid);
        onChange({ user, profile });
    });
}

/** Whether the store should start in a loading state while the initial session check runs. */
export function initialLoadingState(): boolean {
    return true;
}
